import * as THREE from 'three';
import type { ModelPointAxisCandidate, ModelPointAxisCandidates } from '../../../types/sketch/ModelPointAxisTracking';
import type { VisibleModelEdgeHoverOptions, VisibleModelEdgeProjectionRecord } from '../../../types/sketch/VisibleModelEdgeProjection';
import { isModelReferencePointSnapEnabled } from '../../utils';

/**
 * 可见模型投影点的屏幕轴向索引。
 *
 * 只在投影记录、相机或视口变化时重建排序。指针移动通过二分查找进入阈值区间，
 * 再按轴向误差与沿轴距离挑选最近点；不依赖模型边 hover，也不实例化草图点。
 */
export class ModelPointAxisIndex {
    /** 当前投影点数据。 */
    private readonly candidates: ModelPointAxisCandidate[] = [];
    /** 按屏幕 X 排序，用于竖直追踪。 */
    private readonly byX: ModelPointAxisCandidate[] = [];
    /** 按屏幕 Y 排序，用于水平追踪。 */
    private readonly byY: ModelPointAxisCandidate[] = [];
    /** 上一次用于屏幕投影的完整矩阵。 */
    private readonly projectionMatrix = new THREE.Matrix4();
    /** 当前矩阵计算缓存。 */
    private readonly nextProjectionMatrix = new THREE.Matrix4();
    /** 屏幕投影复用向量。 */
    private readonly projectedPoint = new THREE.Vector3();
    /** 上次视口大小。 */
    private readonly viewport = new THREE.Vector2();
    /** 投影记录改变后强制重建屏幕排序。 */
    private dirty = true;
    /** 查询结果对象在指针热路径复用。 */
    private readonly result: ModelPointAxisCandidates = { horizontal: null, vertical: null };

    /**
     * 以完整可见投影记录替换索引数据。
     * @param records 当前会话投影结果。
     */
    public rebuild(records: readonly VisibleModelEdgeProjectionRecord[]): void {
        this.clear();
        for (const record of records) {
            for (const referencePoint of record.referencePoints) {
                this.candidates.push({ record, referencePoint, screenPosition: new THREE.Vector2() });
            }
        }
    }

    /**
     * 查询屏幕轴向阈值内的最佳参考点。
     * @param pointer 当前渲染器像素坐标。
     * @param camera 当前相机。
     * @param viewport 视口像素大小。
     * @param thresholdPx 轴向容差。
     * @param options 模型与点类型开关。
     * @param excludedIds 不得参与捕捉的源对象集合。
     * @returns 复用的查询结果，仅在下一次查询前有效。
     */
    public query(pointer: THREE.Vector2, camera: THREE.Camera, viewport: THREE.Vector2, thresholdPx: number,
        options: VisibleModelEdgeHoverOptions, excludedIds?: ReadonlySet<string>): ModelPointAxisCandidates {
        this.result.horizontal = null;
        this.result.vertical = null;
        if (!options.modelGeometryEnabled || viewport.x <= 0 || viewport.y <= 0) return this.result;
        this.syncScreenCoordinates(camera, viewport);
        this.result.horizontal = this.findNearest(this.byY, 'y', pointer, thresholdPx, options, excludedIds);
        this.result.vertical = this.findNearest(this.byX, 'x', pointer, thresholdPx, options, excludedIds);
        return this.result;
    }

    /**
     * 更新与相机相关的排序；同一视图的连续指针事件直接复用。
     * @param camera 当前相机。
     * @param viewport 当前视口大小。
     */
    private syncScreenCoordinates(camera: THREE.Camera, viewport: THREE.Vector2): void {
        camera.updateWorldMatrix(true, false);
        this.nextProjectionMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        if (!this.dirty && this.projectionMatrix.equals(this.nextProjectionMatrix) && this.viewport.equals(viewport)) return;
        this.projectionMatrix.copy(this.nextProjectionMatrix);
        this.viewport.copy(viewport);
        this.byX.length = 0;
        this.byY.length = 0;
        for (const candidate of this.candidates) {
            this.projectedPoint.copy(candidate.referencePoint.projectedPosition).applyMatrix4(this.projectionMatrix);
            // 不让相机背后或裁剪深度外的点产生跨视口追踪。
            if (!Number.isFinite(this.projectedPoint.x) || this.projectedPoint.z < -1 || this.projectedPoint.z > 1) continue;
            candidate.screenPosition.set((this.projectedPoint.x + 1) * viewport.x / 2, (1 - this.projectedPoint.y) * viewport.y / 2);
            this.byX.push(candidate);
            this.byY.push(candidate);
        }
        this.byX.sort((left, right) => left.screenPosition.x - right.screenPosition.x);
        this.byY.sort((left, right) => left.screenPosition.y - right.screenPosition.y);
        this.dirty = false;
    }

    /**
     * 二分定位轴向区间并在区间内择优，排序规则与草图点追踪一致。
     * @param entries 已按目标轴排序的点。
     * @param axis 用于约束的屏幕轴。
     * @param pointer 指针坐标。
     * @param thresholdPx 捕捉阈值。
     * @param options 点类型门禁。
     * @param excludedIds 被排除的源模型对象。
     * @returns 误差最小且沿轴距离最近的候选。
     */
    private findNearest(entries: readonly ModelPointAxisCandidate[], axis: 'x' | 'y', pointer: THREE.Vector2,
        thresholdPx: number, options: VisibleModelEdgeHoverOptions, excludedIds?: ReadonlySet<string>): ModelPointAxisCandidate | null {
        let lower = 0;
        let upper = entries.length;
        const minimum = pointer[axis] - thresholdPx;
        const maximum = pointer[axis] + thresholdPx;
        while (lower < upper) {
            const middle = (lower + upper) >>> 1;
            if (entries[middle]!.screenPosition[axis] < minimum) lower = middle + 1;
            else upper = middle;
        }
        const otherAxis = axis === 'x' ? 'y' : 'x';
        let best: ModelPointAxisCandidate | null = null;
        let bestError = Infinity;
        let bestDistance = Infinity;
        for (let index = lower; index < entries.length; index += 1) {
            const candidate = entries[index]!;
            if (candidate.screenPosition[axis] > maximum) break;
            const enabled = isModelReferencePointSnapEnabled(candidate.referencePoint.kind, options);
            if (!enabled || excludedIds?.has(candidate.record.sourceMesh.uuid)) continue;
            const error = Math.abs(candidate.screenPosition[axis] - pointer[axis]);
            const distance = Math.abs(candidate.screenPosition[otherAxis] - pointer[otherAxis]);
            if (error < bestError || (error === bestError && distance < bestDistance)) {
                best = candidate;
                bestError = error;
                bestDistance = distance;
            }
        }
        return best;
    }

    /** 清空所有记录及查询结果，解除对源模型和旧投影的引用。 */
    public clear(): void {
        this.candidates.length = 0;
        this.byX.length = 0;
        this.byY.length = 0;
        this.result.horizontal = null;
        this.result.vertical = null;
        this.dirty = true;
    }
}
